import React, { useEffect, useState, useContext } from "react";
import { Link } from 'react-router-dom';

import { Drawer, Input, Flex, Modal, Space, Switch, Table, Tag } from "antd";
import { EditOutlined ,PauseCircleOutlined, PlayCircleOutlined , DeleteOutlined , EditFilled, PlusOutlined, DownOutlined, RedoOutlined, CopyOutlined, ClearOutlined } from '@ant-design/icons';

import { v4 as uuidv4 } from 'uuid';
import { jsonrepair } from 'jsonrepair';
import { flushSync } from 'react-dom';


import { ScenariosContext } from '../index';
import { ActionButton } from "../../../components/elements/Button";
import { DropdownMenu } from "../../../components/elements/DropdownMenu";
import { SortableList } from "../SortableList";
import { defaultParameterNotation, transformDataObject, NestedObjectTable } from "../../../components/elements/JsonObjTable";
import configService from '../../../services/config.service';

import { defaultMenuProps } from '../defaultEventTemplates';
import "./styles.scss";

const { TextArea } = Input;

const generateUniqueId = () => uuidv4();

const templateElemsList = (items) => {
    switch (items.length) {
        case 0:
            return [defaultParameterNotation({
                key: "",
                type: "string",
                required: true,
                exatclyMatch: false,
                value: "",
            })];
        default:
            return items;
    }
}

const scenarioStartPageMatchTypes = [
    {
        label: "exact matches",
        key: "exact_matches",
    },
    {
        label: "matches regex",
        key: "matches_regex",
    },
    {
        label: "contains",
        key: "contains",
    },
];

const scenarioStartPageTypes = [
    {
        label: "hostname",
        key: "hostname",
    },
    {
        label: "page url",
        key: "page_url",
    },
    {
        label: "page path",
        key: "page_path",
    },
];

const defaultScenarioObject = () => ({
    scenarioID: generateUniqueId(),
    scenarioTitle: "",
    active: false,
    urlStartingPoint: "",
    urlStartingPointMatchType: "",
    cookiesAcceptance: {
        active: false,
        cookiesPassed: false,
        steps: [],
    },
    loginInfo: {
        required: false,
        isLoggedIn: false,
        steps: [],
    },
    steps: [],
});

const ConfirmationModalWindow = (props) => {
    const {
        open = false,
        onOk = () => { },
        onCancel = () => { },
        text = "",
    } = props;

    return (
        <Modal
            centered
            title={"Confirm action"}
            open={open}
            onOk={onOk}
            onCancel={onCancel}
            footer={(_, { OkBtn, CancelBtn }) => (
                <Flex gap={5}>
                    <CancelBtn />
                    <OkBtn />
                </Flex>
            )}>
            {text}
        </Modal>
    );
}


const ScenariosMenu = () => {
    const {
        companyId = "",
        configParams = {},
        setConfigParams = () => { },
        pageScenariosDescLink,
        scenariosList = [],
        drawerGlobalState = false,
        setDrawerGlobalState = () => { },
        saveFunction = () => { },
        defaultEventTemplates = [],
        simplifyDLGEventJson = () => { },
        transformDataObject = () => { },
        error = () => { },
    } = useContext(ScenariosContext);


    // Constant variables
    const defaultPlacement = "right";
    const secondDrawerTitle = "Scenario Steps List";
    const thirdDrawerTitle = "DataLayer Template";
    const cookiesDrawerTitle = "Cookies Scenario Steps List";
    const firstDrawerWidth = "75%";
    const secondDrawerWidth = "75%";
    const thirdDrawerWidth = "75%";

    const defaultMatchType = scenarioStartPageMatchTypes[0]?.key;
    const defaultPageType = scenarioStartPageTypes[0]?.key;

    const [jsonBtnText, setJsonBtnText] = useState("Add JSON");
    const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusOutlined />);


    // Drawers control area
    const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
    const [thirdDrawerVisible, setThirdDrawerVisible] = useState(false);
    const [cookiesDrawerVisible, setCookiesDrawerVisible] = useState(false);
    const [loginDrawerVisible, setLoginDrawerVisible] = useState(false);

    const showSecondDrawer = () => setSecondDrawerVisible(true);
    const closeSecondDrawer = () => setSecondDrawerVisible(false);

    const showCookiesDrawer = () => setCookiesDrawerVisible(true);
    const closeCookiesDrawer = () => setCookiesDrawerVisible(false);

    const showLoginDrawer = () => setLoginDrawerVisible(true);
    const closeLoginDrawer = () => setLoginDrawerVisible(false);

    const showThirdDrawer = (item, stepId) => {
        const parametersList = templateElemsList(item);
        setSelectedTemplate(parametersList);
        setSelectedTemplateStr(JSON.stringify(parametersList) || "");
        setSelectedStepId(stepId);
        setThirdDrawerVisible(true);
    };
    const closeThirdDrawer = () => setThirdDrawerVisible(false);


    // Scenarios list modal
    const [curScenariosList, setCurScenariosList] = useState(null);
    const [curScenariosEvents, setCurScenariosEvents] = useState({});

    useEffect(() => {
        setCurScenariosList(scenariosList);
        const scenariosDropdownList = scenariosList.map((elem, idx) => ({
            key: elem?.scenarioID || idx,
            label: elem?.scenarioTitle || `Untitled Scenario ${idx + 1}`,
        }));

        setStepsScenarioOptions(getScenariosListProps({
            listUpdateFunction: setScenarioSteps,
            scenariosDropdownList: scenariosDropdownList,
            stepsType: "steps",
            modalCancelFunction: handleStepsListModalCancel,
            modalStateChangeFunction: setStepsListChangeModalState,
        }));

        setCookiesStepsScenarioOptions(getScenariosListProps({
            listUpdateFunction: setScenarioCookiesSteps,
            scenariosDropdownList: scenariosDropdownList,
            stepsType: "cookies",
            modalCancelFunction: handleCookiesStepsListModalCancel,
            modalStateChangeFunction: setCookiesStepsListChangeModalState,
        }));

        setLoginStepsScenarioOptions(getScenariosListProps({
            listUpdateFunction: setScenarioLoginSteps,
            scenariosDropdownList: scenariosDropdownList,
            stepsType: "login",
            modalCancelFunction: handleLoginStepsListModalCancel,
            modalStateChangeFunction: setLoginStepsListChangeModalState,
        }));

        const scenariosEvents = {};

        scenariosList.forEach(elem => {
            const events = elem?.steps?.filter(step =>
                ["collect_dl_object", "collect_dl_before_navigation"].includes(step?.type));

            scenariosEvents[elem.scenarioID] = events?.map(event => ({
                stepNum: event?.step,
                eventName: event?.value,
            })) || [];
        });

        setCurScenariosEvents(scenariosEvents);
    }, [scenariosList]);

const [pauseModalVisible, setPauseModalVisible] = useState(false);
const [deleteModalVisible, setDeleteModalVisible] = useState(false);
const [targetScenarioId, setTargetScenarioId] = useState(null);

// Called when user clicks the trash icon in the table
const confirmDeleteScenario = (id) => {
    setTargetScenarioId(id);
    setDeleteModalVisible(true);
};

// Called when they click “OK” in the delete scenario popup
const handleDeleteConfirmed = () => {
  if (!targetScenarioId) return;
  flushSync(() => {
    setDeleteModalVisible(false);
    setTargetScenarioId(null);
    scenarioRemove(targetScenarioId);
  });
};

// Delete Scenario logic
const scenarioRemove = (id) => {
  // a) filter out the deleted scenario
  const newScenariosList = curScenariosList.filter(
    s => s.scenarioID !== id
  );

  // b) update local state
  setCurScenariosList(newScenariosList);

  // c) close the detail drawer & any confirmation modal
  setScenarioRemoveConfirmation(false);
  closeSecondDrawer();

  // d) push the change back up into your configParams + trigger save
  const updated = { ...configParams, scenarios: newScenariosList };
  setConfigParams(updated);
  setIsConfigParamsUpdatedBeforeSave(true);
};

// Called when user clicks the pouse icon in the table
const confirmPauseScenario = (id) => {
    setTargetScenarioId(id);
    setPauseModalVisible(true);
};

// Called when they click “OK” in the pause scenario popup
const handlePauseConfirmed = () => {
  if (!targetScenarioId) return;
  flushSync(() => {
    setPauseModalVisible(false);
    setTargetScenarioId(null);
    scenarioPause(targetScenarioId);
  });
};

// Pause Scenario logic
const scenarioPause = (id) => {
  // 1) build the new list with active=false for that one ID
  const newList = curScenariosList.map(s =>
    s.scenarioID === id ? { ...s, active: false } : s
  );

  // 2) update the table
  setCurScenariosList(newList);

  // 3) push it back into your in-memory config and flag for save
  const updated = { ...configParams, scenarios: newList };
  setConfigParams(updated);
  setIsConfigParamsUpdatedBeforeSave(true);

  // 4) close the detail drawer
  closeSecondDrawer();
};

// Activate Scenario logic
const activateScenario = (id) => {
  // 1) build the new list with active=false for that one ID
  const newList = curScenariosList.map(s =>
    s.scenarioID === id ? { ...s, active: true } : s
  );
  
  // 2) update the table
  setCurScenariosList(newList);
  
  // 3) push it back into your in-memory config and flag for save
  const updated = { ...configParams, scenarios: newList };
  setConfigParams(updated);
  setIsConfigParamsUpdatedBeforeSave(true);
  
  // 4) close the detail drawer
  closeSecondDrawer();
};

const headerStyle = {
  background: "#fff",
  backgroundImage: "none",
  boxShadow: "none",
  filter: "none",
};

const scenarioColumns = [
    {
        title: 'Scenario name',
        dataIndex: 'scenarioTitle',
        key: 'scenarioTitle',
        align: 'left',
        onHeaderCell: () => ({ style: headerStyle }),
    },
    {
        title: 'Monitored events',
        dataIndex: 'left',
        key: 'events',
        align: 'left',
        render: (_, record) => (
            <span style={{  fontStyle: "italic" }}>
                {curScenariosEvents[record?.scenarioID]
                    ?.map(event => event?.eventName)
                    .filter(Boolean)
                    .join(", ")}
            </span>
            ),
        onHeaderCell: () => ({ style: headerStyle })
    },
    {
        title: 'Status',
        dataIndex: 'active',
        key: 'left',
        align: 'middle',
        render: (active) => (
            active ? "active"  : "paused"
        ),
        onHeaderCell: () => ({ style: headerStyle })
    },
    {
        title: '',
        key: 'actions',
        align: 'right',
        render: (_, record) => (
            <Space size={14}>
                <DeleteOutlined 
                    style={{ color: "#000", cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteScenario(record?.scenarioID);
                    }}
                />               
                {record.active ? (
                    // if it’s active, show Pause
                    <PauseCircleOutlined
                        style={{ color: "#000", cursor: "pointer" }}
                        onClick={e => {
                        e.stopPropagation();
                        confirmPauseScenario(record.scenarioID);
                        }}
                    />
                    ) : (
                    // if it’s paused, show Play
                    <PlayCircleOutlined
                        style={{ color: "#000", cursor: "pointer" }}
                        onClick={e => {
                        e.stopPropagation();
                        activateScenario(record.scenarioID);
                        }}
                    />
                )}

                 <EditOutlined 
                    style={{color: "#000", cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditScenarioClick(record?.scenarioID);
                    }}
                />
            </Space>
        ),
        onHeaderCell: () => ({ style: headerStyle })
    },
];


    // Scenario edit modal
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [scenarioTitle, setScenarioTitle] = useState("");
    const [scenarioStatus, setScenarioStatus] = useState(false);
    const [scenarioStartPage, setScenarioStartPage] = useState(false);
    const [scenarioStartPageMatchType, setScenarioStartPageMatchType] = useState(null);
    const [scenarioStartPageType, setScenarioStartPageType] = useState(null);
    const [scenarioSteps, setScenarioSteps] = useState([]);

    const [scenarioCookies, setScenarioCookies] = useState(null);
    const [scenarioCookiesStatus, setScenarioCookiesStatus] = useState(false);
    const [scenarioCookiesSteps, setScenarioCookiesSteps] = useState(false);

    const [scenarioLogin, setScenarioLogin] = useState(null);
    const [scenarioLoginStatus, setScenarioLoginStatus] = useState(false);
    const [scenarioLoginSteps, setScenarioLoginSteps] = useState(false);

    const [scenarioEvents, setScenarioEvents] = useState([]);

    // Updating data with selecting a new scenario from the list
    useEffect(() => {
        setScenarioTitle(selectedScenario?.scenarioTitle || "");
        setScenarioStatus(!!selectedScenario?.active);
        setScenarioStartPage(selectedScenario?.urlStartingPoint || "");
        setScenarioStartPageMatchType(selectedScenario?.urlStartingPointMatchType || defaultMatchType);
        setScenarioStartPageType(selectedScenario?.urlStartingPointPageType || defaultPageType);
        setScenarioSteps(selectedScenario?.steps || []);
        setScenarioCookies(selectedScenario?.cookiesAcceptance || {});
        setScenarioLogin(selectedScenario?.loginInfo || {});
        setScenarioEvents(curScenariosEvents[selectedScenario?.scenarioID]);
    }, [selectedScenario]);

    useEffect(() => {
        setScenarioCookiesStatus(!!scenarioCookies?.active);
        setScenarioCookiesSteps(scenarioCookies?.steps || []);
    }, [scenarioCookies]);

    useEffect(() => {
        setScenarioLoginStatus(!!scenarioLogin?.required);
        setScenarioLoginSteps(scenarioLogin?.steps || []);
    }, [scenarioLogin]);


    const menuMatchTypes = {
        items: scenarioStartPageMatchTypes.map(({ key, label }) => ({ key, label })),
        onClick: ({ key }) => {
            setScenarioStartPageMatchType(key);
        },
    };

    const menuPageTypes = {
        items: scenarioStartPageTypes.map(({ key, label }) => ({ key, label })),
        onClick: ({ key }) => {
            setScenarioStartPageType(key);
        },
    };

    // Scenarios list editing area
    const handleEditScenarioClick = (id) => {
        const scenarioObj = curScenariosList.find(elem => elem.scenarioID === id);
        setSelectedScenario(scenarioObj);
        setSelectedTemplateStr("");
        showSecondDrawer();
    };

    const handleAddScenarioClick = () => {
        const newScenario = defaultScenarioObject();
        setSelectedScenario(newScenario);
        setCurScenariosList([...curScenariosList, newScenario]);
        setSelectedTemplateStr("");
        showSecondDrawer();
    };

    const handleManageConsentSettings = () => { // TODO
        // const newScenario = defaultScenarioObject();
        // setSelectedScenario(newScenario);
        // setCurScenariosList([...curScenariosList, newScenario]);
        // setSelectedTemplateStr("");
        // showSecondDrawer();
    };

    const [scenarioRemoveConfirmation, setScenarioRemoveConfirmation] = useState(false);


    // Steps list editing area
    const [stepsScenarioOptions, setStepsScenarioOptions] = useState({});
    const [cookiesStepsScenarioOptions, setCookiesStepsScenarioOptions] = useState({});
    const [loginStepsScenarioOptions, setLoginStepsScenarioOptions] = useState({});

    const [stepsListChangeModalState, setStepsListChangeModalState] = useState(false);
    const [cookiesStepsListChangeModalState, setCookiesStepsListChangeModalState] = useState(false);
    const [loginStepsListChangeModalState, setLoginStepsListChangeModalState] = useState(false);

    const handleStepsListModalCancel = () => handleStepsListModalChangeState({
        newState: false,
        modalStateChangeFunction: setStepsListChangeModalState,
    });
    const handleCookiesStepsListModalCancel = () => handleStepsListModalChangeState({
        newState: false,
        modalStateChangeFunction: setCookiesStepsListChangeModalState,
    });
    const handleLoginStepsListModalCancel = () => handleStepsListModalChangeState({
        newState: false,
        modalStateChangeFunction: setLoginStepsListChangeModalState,
    });

    const [stepsListChangeModalOkFunction, setStepsListChangeModalOkFunction] = useState(() => { });

    const handleStepsListModalChangeState = (props) => {
        const {
            newState = false,
            okFunction = () => { },
            modalStateChangeFunction = () => { },
        } = props;

        modalStateChangeFunction(newState);
        setStepsListChangeModalOkFunction(() => okFunction);
    };

    const getScenariosListProps = (props) => {
        const {
            listUpdateFunction = () => { },
            scenariosDropdownList = [],
            stepsType = "",
            modalCancelFunction = () => { },
            modalStateChangeFunction = () => { },
        } = props;

        return {
            items: scenariosDropdownList,
            onClick: (e) => handleStepsListModalChangeState({
                newState: true,
                okFunction: () => copyStepsListFromAnotherScenario({
                    scenarioId: e?.key,
                    listUpdateFunction,
                    stepsType,
                    modalCancelFunction,
                }),
                modalStateChangeFunction,
            }),
        };
    };

    const clearStepsList = (props) => {
        const {
            listUpdateFunction = () => { },
            modalCancelFunction = () => { },
        } = props;

        listUpdateFunction([]);

        modalCancelFunction();
    };

    const copyStepsListFromAnotherScenario = (props) => {
        const {
            scenarioId = "",
            listUpdateFunction = () => { },
            stepsType = "",
            modalCancelFunction = () => { },
        } = props;

        const scenario = curScenariosList?.find(elem => elem?.scenarioID === scenarioId);
        let steps = [];

        switch (stepsType) {
            case "steps":
                steps = scenario?.steps || [];
                break;
            case "cookies":
                steps = scenario?.cookiesAcceptance?.steps || [];
                break;
            case "login":
                console.log(scenario?.loginInfo);
                steps = scenario?.loginInfo?.steps || [];
                break;
        }

        listUpdateFunction(steps);

        modalCancelFunction();
    };


    // DataLayer template modal
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedTemplateStr, setSelectedTemplateStr] = useState(null);

    const menuProps = {
        items: defaultMenuProps,
        onClick: (e) => addDefaultEcommerceObject(e?.key),
    };

    const clearJSON = () => {
        setSelectedTemplate(null);
        setSelectedTemplateStr(null);
    };

    const addDefaultEcommerceObject = (eventName) => {
        const obj = defaultEventTemplates[eventName];
        setSelectedTemplate(obj);
        setSelectedTemplateStr(JSON.stringify(obj));
    };


    // Edit JSON modal window
    const [dlEditModalTitle, setDlEditModalTitle] = useState("");
    const [dlEditModalState, setDlEditModalState] = useState(false);
    const [dlEditModalContent, setDlEditModalContent] = useState(false);
    const [editedTextfieldValue, setEditedTextfieldValue] = useState("");
    const defaultEventTemplate = { "": "" };

    useEffect(() => {
        const initialTemplate =
            selectedTemplate?.length === 1 &&
            selectedTemplate[0].key === "" &&
            selectedTemplate[0].value === "";

        const text = initialTemplate ? "Add JSON" : "Edit JSON";
        const icon = initialTemplate ? <PlusOutlined /> : <EditFilled />;

        setJsonBtnText(text);
        setJsonBtnIcon(icon);
    }, [selectedTemplate]);


    // Modal window event handlers
    const handleEditDlModalOk = () => {
        try {
            const value = jsonrepair(editedTextfieldValue || JSON.stringify(defaultEventTemplate));
            saveEditedDlObjectData(value);
        }
        catch (err) {
            console.log(err);
            error("Incorrect value! Please check the JSON object and try again.");
        }
    };
    const saveEditedDlObjectData = (value) => {
        const transformedTextFieldValue = transformDataObject(JSON.parse(value));
        setSelectedTemplate(transformedTextFieldValue);
        setSelectedTemplateStr(JSON.stringify(transformedTextFieldValue));

        handleSaveTemplate(transformedTextFieldValue);

        setDlEditModalState(false);
    };
    const handleEditDlModalCancel = () => setDlEditModalState(false);

    const handleEditTemplateEvent = () => {
        setDlEditModalContent(defaultModalContent());
        setDlEditModalState(true);
    };

    const defaultModalContent = () => {
        const placeholderObj = {
            "event": "event_name",
            "ecommerce": {
                "currency": "string",
                "value": "number",
                "items": [
                    {
                        "item_id": "string",
                        "item_name": "string",
                    }
                ]
            }
        };
        const placeholderText = JSON.stringify(placeholderObj, undefined, 2);

        const defaultValueTemplate = simplifyDLGEventJson(selectedTemplate);
        const defaultValueStr = JSON.stringify(defaultValueTemplate, null, 2);
        setEditedTextfieldValue(defaultValueStr);

        setDlEditModalTitle("Template DataLayer object");

        return (
            <>
                <p>Add your dataLayer template in the box below.</p>
                <TextArea
                    name="datalayer_object_input"
                    key={editedTextfieldValue}
                    rows={12}
                    placeholder={placeholderText}
                    defaultValue={defaultValueStr}
                    onInput={(e) => setEditedTextfieldValue(e.target.value)}
                    style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        backgroundColor: "#f7f7f7",
                        border: "1px solid #ccc",
                        padding: "10px",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap",
                    }}
                />
            </>
        );
    };


    // Saving data
    const handleSaveTemplate = (template = []) => {
        const updatedSteps = scenarioSteps.map((elem) => {
            switch (elem.stepId === selectedStepId) {
                case true:
                    elem.dataLayerObjToFind = template;
                    return elem;

                default:
                    return elem;
            }
        });

        const templateSimplified = simplifyDLGEventJson(template);
        const templateStr = JSON.stringify(templateSimplified);
        setEditedTextfieldValue(templateStr);

        setSelectedTemplate(template);
        setSelectedTemplateStr(JSON.stringify(template));
        handleSaveSteps(updatedSteps);
    };

    const handleSaveSteps = (steps = []) => {
        setScenarioSteps(steps);

        const newScenarioObj = selectedScenario;
        newScenarioObj.steps = steps;

        setSelectedScenario(newScenarioObj);

        handleSaveScenarios();
    };

    const handleSaveScenarios = () => {
        let newScenariosList = [];

        if (selectedScenario) {
            const newScenarioObj = selectedScenario;
            newScenarioObj.scenarioTitle = scenarioTitle;
            newScenarioObj.active = scenarioStatus;
            newScenarioObj.urlStartingPoint = scenarioStartPage;
            newScenarioObj.urlStartingPointMatchType = scenarioStartPageMatchType;
            newScenarioObj.urlStartingPointPageType = scenarioStartPageType;
            newScenarioObj.steps = scenarioSteps;

            const newCookiesObj = { ...scenarioCookies };
            newCookiesObj.active = scenarioCookiesStatus;
            newCookiesObj.steps = scenarioCookiesSteps;
            setScenarioCookies(newCookiesObj);

            const newLoginObj = { ...scenarioLogin };
            newLoginObj.required = scenarioLoginStatus;
            newLoginObj.steps = scenarioLoginSteps;
            setScenarioLogin(newLoginObj);

            newScenarioObj.cookiesAcceptance = newCookiesObj;
            newScenarioObj.loginInfo = newLoginObj;

            newScenariosList = curScenariosList.map(elem => {
                switch (selectedScenario.scenarioID === elem.scenarioID) {
                    case true:
                        return newScenarioObj;
                    default:
                        return elem;
                }
            });
        }

        setCurScenariosList(newScenariosList);
    };

    const handleSaveAction = (level) => {
        switch (level) {
            case "templates":
                handleSaveTemplate(selectedTemplate);
                closeThirdDrawer();
                break;
            case "steps":
                handleSaveSteps(scenarioSteps);
                closeSecondDrawer();
                break;
            case "cookies":
                handleSaveScenarios();
                closeCookiesDrawer();
                break;
            case "login":
                handleSaveScenarios();
                closeLoginDrawer();
                break;
            case "scenarios":
                handleSaveScenarios();
                setDrawerGlobalState(false);
                break;
        }

        const newConfigParams = { ...configParams };
        newConfigParams.scenarios = curScenariosList;

        setConfigParams(newConfigParams);
        setIsConfigParamsUpdatedBeforeSave(true);
    };


    // Data saving functionality control
    const [isConfigParamsUpdatedBeforeSave, setIsConfigParamsUpdatedBeforeSave] = useState(false);

    useEffect(() => {
        if (isConfigParamsUpdatedBeforeSave) {
            saveFunction();
            setIsConfigParamsUpdatedBeforeSave(false);
        }
    }, [isConfigParamsUpdatedBeforeSave]);


    return (
        <div>
            {/* Scenario drawer menu. */}
            <Drawer
                className="scenarios-drawer" 
                placement={defaultPlacement}
                onClose={() => setDrawerGlobalState(false)}
                open={drawerGlobalState}
                closeIcon={null}
                closable={true}
                width={firstDrawerWidth}
            >
                <div className={`modal-elems-rows scenarios-modal-padding`}>
                    <div>
                        <h2 className='header-letter-spacing'>Overview DataLayer Guard scenario’s</h2>
                    </div>
                    <div style={{ marginBottom: 0 }} className="container-guide modal-panel-button-row">
                        <Link className="modal-guide-banner blue-bg" target="_blank" to={pageScenariosDescLink} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="file-icon" style={{ color: '$cc-blue' }}>
                                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g filter="url(#filter0_d_190_157)">
                                        <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                        <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                        <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                        <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                    </g>
                                    <defs>
                                        <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                            <feOffset dy="1.74496" />
                                            <feGaussianBlur stdDeviation="0.872482" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                        </filter>
                                    </defs>
                                </svg>
                            </div>
                            <div className="panel-button-text">
                                <div className="panel-button-desc">Need help? Check our <u>implementation guide</u> for detailed configuration instructions and best practices for DataLayer Guard. Question not covered? Please <u>get in contact</u>.</div>
                            </div>
                        </Link>
                    </div>
                    <div className="modal-text">
                        <p>
                            This page provides an overview of all scenarios that DataLayer Guard should follow to collect dataLayer events. Each scenario should replicate the steps taken by a typical user.
                        </p>
                        <p>
                            You can configure multiple scenarios to ensure comprehensive event monitoring.
                        </p>                 
                    </div>

                    {/* Table with scenarios list. */}
                    <div className="no-sep-header">
                        <Table
                            size="small"
                            columns={scenarioColumns}
                            dataSource={curScenariosList}
                            rowKey="scenarioID"
                            rowClassName="scenarios-list-row"
                            onRow={(record) => ({
                                onClick: () => handleEditScenarioClick(record?.scenarioID),
                            })}
                            pagination={false}
                        />
                    </div>
                    <div
                    style={{
                        marginTop: "32px",
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "flex-end",
                        gap: "18px"
                    }}
                    >

                    {/* add scenario button */}                     
                   <button
                        style={{
                            background: "#cfff5e",
                            color: "#232821",
                            border: "none",
                            borderRadius: "100px",
                            fontWeight: 700,
                            fontSize: "12px",
                            padding: "5px 20px",
                            boxShadow: "0px 4px 16px #B7CB2B40",
                            cursor: "pointer",
                            outline: "none",
                            letterSpacing: "0.1px",
                            marginLeft: "0px",
                            marginRight: "12px",
                            transition: "background 0.15s, box-shadow 0.15s"
                        }}
                        onClick={handleAddScenarioClick}
                        >
                        + add scenario
                        </button>
                     
                    {/* manage consent settings button */}           
                    <button
                        style={{
                            background: "transparent",
                            color: "#232821",
                            border: "none",
                            fontWeight: 700,
                            fontSize: "12px",
                            padding: "8px 0", 
                            marginLeft: "18px",
                            letterSpacing: "0.1px",
                            position: "relative",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "none",
                            lineHeight: 1.2,
                            minWidth: "auto"
                        }}
                        onClick={handleManageConsentSettings}
                        >
                        + manage consent settings
                        <span style={{
                            display: "block",
                            width: "100%",
                            height: "3px",
                            background: "#cfff5e",       // Neon green
                            borderRadius: "2px",
                            position: "absolute",
                            left: 0,
                            bottom: 0
                        }} />
                    </button>
                    </div>
                    <Modal
                        centered
                        open={deleteModalVisible}
                        title="Delete this scenario?"
                        onOk={handleDeleteConfirmed}
                        onCancel={() => {
                            setDeleteModalVisible(false);
                            setTargetScenarioId(null);
                        }}
                        okButtonProps={{ danger: true }}
                        >
                        Are you sure you want to delete this scenario?
                    </Modal>
                    <Modal
                        centered
                        open={pauseModalVisible}
                        title="Pause this scenario?"
                        onOk={handlePauseConfirmed}
                        onCancel={() => {
                            setPauseModalVisible(false);
                            setTargetScenarioId(null);
                        }}
                        okButtonProps={{ danger: true }}
                        >
                        <p>Are you sure you want to pause this scenario?</p>
                    </Modal>
                </div>

                {/* Scenario steps modal window. */}
                <Drawer
                    className="scenarios-drawer"
                    placement={defaultPlacement}
                    onClose={() => {   
                        handleSaveAction("steps");   // auto‐save
                        closeSecondDrawer();
                    }}
                    open={secondDrawerVisible}
                    closeIcon={null}
                    closable={true}
                    width={secondDrawerWidth}
                >
                    <ConfirmationModalWindow
                        open={stepsListChangeModalState}
                        onOk={stepsListChangeModalOkFunction}
                        onCancel={handleStepsListModalCancel}
                        text={"Are you sure you want to update the steps list?"}
                    />
                    
                    <div key={isConfigParamsUpdatedBeforeSave} className={`modal-elems-rows scenarios-modal-padding`}>
                        <div>
                            <h2 className='header-letter-spacing'>Set-up details for DataLayer Guard scenario</h2>
                         </div>
                            {/* {scenarioEvents?.map((event, idx) => (
                                event?.eventName ?
                                    <Tag key={idx}>{event?.stepNum} - {event?.eventName}</Tag>
                                    : undefined
                            ))}
                        </div> */}
                    <div style={{ marginBottom: 0 }} className="container-guide modal-panel-button-row">
                                <Link className="modal-guide-banner blue-bg" target="_blank" to={pageScenariosDescLink} style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <div className="file-icon" style={{ color: '$cc-blue' }}>
                                        <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g filter="url(#filter0_d_190_157)">
                                                <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                                <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                                <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                                <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                                <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                                <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                            </g>
                                            <defs>
                                                <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                    <feOffset dy="1.74496" />
                                                    <feGaussianBlur stdDeviation="0.872482" />
                                                    <feComposite in2="hardAlpha" operator="out" />
                                                    <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                                </filter>
                                            </defs>
                                        </svg>
                                    </div>
                                    <div className="panel-button-text">
                                        <div className="panel-button-desc">Need help? Check our <u>implementation guide</u> for detailed configuration instructions and best practices for DataLayer Guard. Question not covered? Please <u>get in contact</u>.</div>
                                    </div>
                                </Link>
                            </div>
                            <div className="modal-text">
                                <p>
                                    This page provides the details of the DataLayer Guard scenario. Make sure the scenario replicates the steps taken by an actual user. Please navigate back to the overview to add more scenarios.
                                </p>                
                        </div>
                        
                        {/* Scenario title and starting point. */}
                        <Space size={40} direction="vertical" style={{ width: "100%" }}>
                            <Space direction="vertical" size={4} >
                                <h3 className="modal-category">Scenario name</h3>
                                <div className="modal-text">Please navigate back to the overview to add more scenarios.</div>
                                <div style={{ marginTop: 16  }}>
                                    <Input
                                        name="scenario_title"
                                        value={scenarioTitle}
                                        style={{
                                            width: "10rem",
                                        }}
                                        onChange={(e) => setScenarioTitle(e.target.value)}
                                    />
                                </div>
                            </Space>
                            <Space direction="vertical" size={4}>
                                <h3 className="modal-category">Starting point for scenario</h3>
                                <div className="modal-text">Please navigate back to the overview to add more scenarios.</div>
                                <div style={{ marginTop: 16  }}>
                                <Space size={5}>
                                    <DropdownMenu
                                        type="default"
                                        text={
                                            scenarioStartPageTypes.find(e => e.key === scenarioStartPageType)
                                            ?.label || "Select page type"
                                        }
                                        menuProps={menuPageTypes}
                                        icon={<DownOutlined />}
                                        customStyle={{
                                            background: "#fff",
                                            color: "rgba(0,0,0,0.85)",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: 6,
                                            height: 32,
                                            padding: "4px 11px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                    />
                                    <DropdownMenu
                                        type="default"
                                        text={
                                            scenarioStartPageMatchTypes.find(e => e.key === scenarioStartPageMatchType)
                                            ?.label || "Select match type"
                                        }
                                        menuProps={menuMatchTypes}
                                        icon={<DownOutlined />}
                                        customStyle={{
                                            background: "#fff",
                                            color: "rgba(0,0,0,0.85)",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: 6,
                                            height: 32,
                                            padding: "4px 11px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                        />
                                    <Input
                                        name="scenario_start_page"
                                        value={scenarioStartPage} 
                                        placeholder="/tag-monitor"
                                        style={{
                                            width: "20rem",
                                        }}
                                        onChange={(e) => setScenarioStartPage(e.target.value)}
                                    />
                                </Space>
                                </div>
                            </Space>
                        </Space>

                        {/* Dynamically sortable steps list. */}
                        <Space direction="vertical" size={4}>
                            <h3 className="modal-category">Steps to take</h3>
                            <div className="modal-text">Please navigate back to the overview to add more scenarios.</div>
                            <div style={{ marginTop: 16  }}>
                                <SortableList
                                    items={scenarioSteps}
                                    handleDataLayerObjEdit={showThirdDrawer}
                                    handleStepsUpdate={setScenarioSteps}
                                />
                            </div>
                        </Space>
                    </div>

                    {/* Modal window confirmation for deleting step. */}
                    <Modal
                        centered
                        open={scenarioRemoveConfirmation}
                        title="Confirm"
                        onOk={() => scenarioRemove(selectedScenario?.scenarioID)}
                        onCancel={() => setScenarioRemoveConfirmation(false)}
                        footer={(_, { OkBtn, CancelBtn }) => (
                            <Flex gap={5}>
                                <CancelBtn />
                                <OkBtn />
                            </Flex>
                        )}
                    >
                        <p>Do you want to delete this scenario?</p>
                    </Modal>

                    {/* DataLayer object template edit modal window. */}
                    <Drawer
                        title={thirdDrawerTitle}
                        placement={defaultPlacement}
                        onClose={closeThirdDrawer}
                        open={thirdDrawerVisible}
                        closable={true}
                        width={thirdDrawerWidth}
                    >                 
                        {/* Modal window for editing the JSON-object representation of the template. */}
                        <Modal
                            centered
                            title={dlEditModalTitle}
                            open={dlEditModalState}
                            onOk={handleEditDlModalOk}
                            onCancel={handleEditDlModalCancel}
                            footer={(_, { OkBtn, CancelBtn }) => (
                                <Flex gap={5}>
                                    <CancelBtn />
                                    <OkBtn />
                                </Flex>
                            )}>
                            {dlEditModalContent}
                        </Modal>

                        <div className="modal-elems-rows scenarios-modal-padding">
                            <div>
                                <h2 className="header-submodal header-letter-spacing">
                                    DataLayer event template
                                </h2>
                            </div>

                            <div>
                                <h3 className="modal-category fw-bold">
                                    Add event details
                                </h3>
                                <p className='mb-0'>
                                    To enable monitoring for this event, details on the dataLayer event should be
                                    shared in the table below.
                                    <br></br>
                                    For a quick start you can can copy / paste your JSON object via the button below.
                                </p>
                            </div>

                            {/* Buttons to edit template, add ecommerce event or clear template content. */}
                            <Flex gap={10}>
                                <ActionButton
                                    text={jsonBtnText}
                                    type=""
                                    icon={jsonBtnIcon}
                                    onClickFunction={handleEditTemplateEvent}
                                    width={120}
                                />
                                <DropdownMenu
                                    text="Add ecommerce event"
                                    type="primary"
                                    disabled={selectedTemplate?.length > 1}
                                    menuProps={menuProps}
                                    loading={false}
                                    style={{ padding: "12px 20px" }}
                                    icon={<DownOutlined />}
                                />
                                <ActionButton
                                    text="Clean JSON"
                                    type=""
                                    icon={<RedoOutlined />}
                                    onClickFunction={clearJSON}
                                />
                            </Flex>

                            {/* JSON object structure table */}
                            <Space>
                                <NestedObjectTable
                                    key={selectedStepId}
                                    data={selectedTemplateStr}
                                    updateTemplateFunction={setSelectedTemplate}
                                    setEditedEventTemplateNewVersion={setSelectedTemplate}
                                />
                            </Space>
                        </div>

                        {/* Save button for dataLayer object template */}
                        <div className="drawer-buttons-area">
                            <div className="drawer-buttons-area-shad"></div>
                            <div className="drawer-buttons-area-button">
                                <ActionButton
                                    size="large"
                                    type="primary"
                                    width={140}
                                    onClickFunction={() => handleSaveAction("templates")}
                                >
                                    Save
                                </ActionButton>
                            </div>
                        </div>

                    </Drawer>


                    {/* Cookies acceptance scenario steps modal. */}
                    <Drawer
                        title={cookiesDrawerTitle}
                        placement={defaultPlacement}
                        onClose={closeCookiesDrawer}
                        open={cookiesDrawerVisible}
                        closable={true}
                        width={thirdDrawerWidth}
                    >
                        <ConfirmationModalWindow
                            open={cookiesStepsListChangeModalState}
                            onOk={stepsListChangeModalOkFunction}
                            onCancel={handleCookiesStepsListModalCancel}
                            text={"Are you sure you want to update the steps list?"}
                        />

                        <div key={isConfigParamsUpdatedBeforeSave} className={`modal-elems-rows scenarios-modal-padding`}>
                            <div>

                                <h2 className='header-letter-spacing'>Cookies Acceptance Scenario Steps</h2>
                            </div>

                            {/* Scenario title and starting point. */}
                            <Space size={25}>
                                {/* Switch for scenario status. */}
                                <Space>
                                    <Switch
                                        value={scenarioCookiesStatus}
                                        onChange={(value) => setScenarioCookiesStatus(value)}
                                    />
                                    <label>{scenarioCookiesStatus ? "Required" : "Not required"}</label>
                                </Space>
                                <Space>
                                    {/* Copy steps from another scenario */}
                                    <DropdownMenu
                                        text="Import steps"
                                        menuProps={cookiesStepsScenarioOptions}
                                        type="primary"
                                        loading={false}
                                        icon={<CopyOutlined />}
                                        iconPosition="start"
                                    />
                                    {/* Clear steps list */}
                                    <ActionButton
                                        text={<Space><ClearOutlined />Clear steps</Space>}
                                        onClickFunction={() =>
                                            handleStepsListModalChangeState({
                                                newState: true,
                                                okFunction: () => clearStepsList({
                                                    listUpdateFunction: setScenarioCookiesSteps,
                                                    modalCancelFunction: handleCookiesStepsListModalCancel,
                                                }),
                                                modalStateChangeFunction: setCookiesStepsListChangeModalState,
                                            })
                                        }
                                    />
                                </Space>
                            </Space>

                            {/* Dynamically sortable steps list. */}
                            <div style={{ paddingBottom: "5rem" }}>
                                <h3 className="modal-category">Steps</h3>
                                <div style={{ margin: "auto" }}>
                                    <SortableList
                                        items={scenarioCookiesSteps}
                                        handleDataLayerObjEdit={() => { }}
                                        handleStepsUpdate={setScenarioCookiesSteps}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Save button for scenario steps list */}
                        <div className="drawer-buttons-area">
                            <div className="drawer-buttons-area-shad"></div>
                            <div className="drawer-buttons-area-button">
                                <ActionButton
                                    size="large"
                                    type="primary"
                                    width={140}
                                    onClickFunction={() => handleSaveAction("cookies")}
                                >
                                    Save
                                </ActionButton>
                            </div>
                        </div>

                    </Drawer>


                    {/* Login scenario steps modal. */}
                    <Drawer
                        className="scenarios-drawer" 
                        placement={defaultPlacement}
                        onClose={closeLoginDrawer}
                        open={loginDrawerVisible}
                        closeIcon={null}
                        closable={true}
                        width={firstDrawerWidth}
                    >
                        <ConfirmationModalWindow
                            open={loginStepsListChangeModalState}
                            onOk={stepsListChangeModalOkFunction}
                            onCancel={handleLoginStepsListModalCancel}
                            text={"Are you sure you want to update the steps list?"}
                        />

                        <div key={isConfigParamsUpdatedBeforeSave} className={`modal-elems-rows scenarios-modal-padding`}>
                            <div>
                                <h2 className='header-letter-spacing'>Set-up details for login scenario</h2>
                            </div>
                            <div style={{ marginBottom: 0 }} className="container-guide modal-panel-button-row">
                                <Link className="modal-guide-banner blue-bg" target="_blank" to={pageScenariosDescLink} style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <div className="file-icon" style={{ color: '$cc-blue' }}>
                                        <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g filter="url(#filter0_d_190_157)">
                                                <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                                <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                                <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                                <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                                <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                                <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                                <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                            </g>
                                            <defs>
                                                <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                    <feOffset dy="1.74496" />
                                                    <feGaussianBlur stdDeviation="0.872482" />
                                                    <feComposite in2="hardAlpha" operator="out" />
                                                    <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                                </filter>
                                            </defs>
                                        </svg>
                                    </div>
                                    <div className="panel-button-text">
                                        <div className="panel-button-desc">Need help? Check our <u>implementation guide</u> for detailed configuration instructions and best practices for DataLayer Guard. Question not covered? Please <u>get in contact</u>.</div>
                                    </div>
                                </Link>
                            </div>
                            <div className="modal-text">
                                <p>
                                    This page provides an overview of login scenario that DataLayer Guard should follow to sussesfully login. Each step should replicate the step taken by a typical user.
                                </p>                
                            </div>
                            {/* Scenario title and starting point. */}


                            {/* Dynamically sortable steps list. */}
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                <h3 className="modal-category">Steps to take for successful login</h3>
                                <div style={{ width: "100%"}}>  
                                    <SortableList
                                        items={scenarioLoginSteps}
                                        handleDataLayerObjEdit={() => { }}
                                        handleStepsUpdate={setScenarioLoginSteps}
                                    />
                                </div>
                            </Space>
                        </div>

                        {/* Save button for scenario steps list */}
                        <div className="drawer-buttons-area">
                            <div className="drawer-buttons-area-shad"></div>
                            <div className="drawer-buttons-area-button">
                                <ActionButton
                                    size="large"
                                    type="primary"
                                    width={140}
                                    onClickFunction={() => handleSaveAction("login")}
                                >
                                    Save
                                </ActionButton>
                            </div>
                        </div>
                    </Drawer>
                </Drawer>
            </Drawer>
        </div>
    );
};

export default ScenariosMenu;
